/* eslint-disable @typescript-eslint/no-deprecated */
import { Fragment, useState, useEffect } from "react";
import { Combobox, Dialog, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import {
  DocumentPlusIcon,
  Squares2X2Icon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "@/shared/hooks/useCurrentUser";
import {
  canSubmitUseCases,
} from "@/types/auth";

function classNames(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}

interface ICommandItem {
  id: string;
  name: string;
  category: string;
  url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  shortcut?: string;
}

const STATIC_COMMANDS: ICommandItem[] = [
  {
    id: "new-use-case",
    name: "Submit New Use Case",
    category: "Actions",
    url: "/use-cases/new",
    icon: DocumentPlusIcon,
    shortcut: "N",
  },
  {
    id: "dashboard",
    name: "Go to Dashboard",
    category: "Navigation",
    url: "/",
    icon: Squares2X2Icon,
    shortcut: "D",
  },
  {
    id: "browse-tools",
    name: "Browse AI Tools",
    category: "Navigation",
    url: "/ai-tools",
    icon: WrenchScrewdriverIcon,
    shortcut: "T",
  },
  {
    id: "view-use-cases",
    name: "View All Use Cases",
    category: "Navigation",
    url: "/use-cases",
    icon: DocumentTextIcon,
    shortcut: "U",
  },
  {
    id: "approvals",
    name: "Review Approvals",
    category: "Actions",
    url: "/approvals",
    icon: ClipboardDocumentCheckIcon,
    shortcut: "A",
  },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const canCreateUseCases = canSubmitUseCases(user);

  const filteredByPermissions = canCreateUseCases
    ? STATIC_COMMANDS
    : STATIC_COMMANDS.filter((command) => command.id !== "new-use-case");

  useEffect(() => {
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((open) => !open);
      }
    };

    window.addEventListener("keydown", onKeydown);
    return () => {
      window.removeEventListener("keydown", onKeydown);
    };
  }, []);

  const filteredCommands =
    query === ""
      ? filteredByPermissions
      : filteredByPermissions.filter((command) => {
          return command.name.toLowerCase().includes(query.toLowerCase());
        });

  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => { setQuery(""); }}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
              <Combobox
                onChange={(command: ICommandItem | null) => {
                  if (!command) return;
                  setOpen(false);
                  navigate(command.url);
                }}
              >
                <div className="relative">
                  <MagnifyingGlassIcon
                    className="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <Combobox.Input
                    className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                    placeholder="Search commands... (Cmd+K)"
                    onChange={(event) => { setQuery(event.target.value); }}
                  />
                </div>

                {filteredCommands.length > 0 && (
                  <Combobox.Options
                    static
                    className="max-h-72 scroll-py-2 overflow-y-auto py-2 text-sm text-gray-800"
                  >
                    {filteredCommands.map((command) => (
                      <Combobox.Option
                        key={command.id}
                        value={command}
                        className={({ active }) =>
                          classNames(
                            "cursor-default select-none px-4 py-2",
                            active && "bg-primary-600 text-white"
                          )
                        }
                      >
                        {({ active }) => (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <command.icon
                                className={classNames(
                                  "mr-3 h-5 w-5 flex-none",
                                  active ? "text-white" : "text-gray-400"
                                )}
                                aria-hidden="true"
                              />
                              <span className={active ? "text-white" : ""}>
                                {command.name}
                              </span>
                            </div>
                            {command.shortcut && (
                              <span
                                className={classNames(
                                  "ml-3 flex-none text-xs font-semibold",
                                  active ? "text-primary-100" : "text-gray-400"
                                )}
                              >
                                ⌘{command.shortcut}
                              </span>
                            )}
                          </div>
                        )}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                )}

                {query !== "" && filteredCommands.length === 0 && (
                  <p className="p-4 text-sm text-gray-500">
                    No commands found.
                  </p>
                )}
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
